import React, { useEffect } from "react";
import { langPath, switchLangUrl, saveLangPref } from "./utils/lang";
import { setPageMeta } from "./utils/meta";

export default function PrivacyPolicy({ language = 'th' }) {
  useEffect(() => { setPageMeta(language, '/privacy'); }, [language]);

  const root = langPath(language, '/');
  const isEn = language === 'en';
  const isTh = language === 'th';

  /* 言語別テキスト */
  const pageTitle = isTh ? "นโยบายความเป็นส่วนตัว" : isEn ? "Privacy Policy" : "プライバシーポリシー";
  const backLink = isTh ? "← กลับไปหน้าหลัก" : isEn ? "← Back to Top" : "← トップに戻る";
  const sep = language === 'jp' ? "：" : ": ";

  const leadText = isTh
    ? `Patlush Co., Ltd. (ต่อไปนี้เรียกว่า "PATLUSH") จัดการข้อมูลส่วนบุคคลของคุณผ่านนโยบายความเป็นส่วนตัวนี้ ซึ่งใช้กับบริการ "Nunt" และเว็บไซต์ที่เราดำเนินการ นโยบายความเป็นส่วนตัวนี้สรุปแนวทางพื้นฐานของเราในการจัดการข้อมูลส่วนบุคคลตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 และกฎหมายอื่น ๆ ที่เกี่ยวข้อง`
    : isEn
    ? `Patlush Co., Ltd. manages your personal information through this Privacy Policy, which applies to the "Nunt" service and website operated by us. This Privacy Policy outlines our fundamental approach to handling personal information in compliance with the Personal Data Protection Act B.E. 2562 (2019) and other applicable laws.`
    : `Patlush（以下「弊社」）は、このプライバシーポリシーにより、弊社が運営する「Nunt」サービスおよびWebサイトを通じてお客様の個人情報を管理します。このプライバシーポリシーは、個人情報の取り扱いに関する弊社の基本方針を示すもので、タイの個人情報保護法（PDPA）B.E. 2562 (2019)およびその他適用される法律に従って策定されています。`;

  const sectionsJa = [
    {
      title: "1. 収集する個人情報の種類について",
      items: [
        { label: "個人情報", text: "お名前、メールアドレス、電話番号、会社名など、弊社のサービスを利用する際にご提供いただく情報。" },
        { label: "ログデータ", text: "弊社のウェブサイトやサービスをご利用の際に自動的に収集される情報。これにはIPアドレス、ブラウザの種類、使用するオペレーティングシステム、訪問したページなどが含まれます。" },
        { label: "クッキーと同様の技術", text: "弊社のウェブサイトとの対話に関するデータを収集するために、クッキーや類似の技術を使用します。" },
      ]
    },
    {
      title: "2. 個人情報の使用目的について",
      items: [
        { label: "サービスの提供と改善", text: "ご提供いただいた情報を、サービスの提供およびウェブサイトやサービスの改善に利用します。" },
        { label: "コミュニケーション", text: "お問い合わせへの対応、カスタマーサポート、資料の送付、サービスに関する最新情報の提供などに利用します。" },
        { label: "マーケティング", text: "お客様の同意を得た上で、弊社の製品やサービスに関するマーケティングコミュニケーションを送信します。いつでもこれらのコミュニケーションをオプトアウトすることができます。" },
        { label: "法的コンプライアンス", text: "法的義務の遵守や、政府機関からの合法的な要求に応じるために利用します。" },
      ]
    },
    {
      title: "3. 個人情報の共有・開示について",
      items: [
        { label: "サービスプロバイダー", text: "サービスの提供に関わる第三者サービスプロバイダーに、必要に応じて情報を提供します。これらのプロバイダーは、情報の保護に関する義務を負います。" },
        { label: "法的要件", text: "法的義務に基づき、または弊社の権利、プライバシー、安全、財産を保護するため、情報を開示することがあります。" },
        { label: "お客様の同意", text: "明示的な同意がある場合、第三者と情報を共有することがあります。" },
      ]
    },
    {
      title: "4. 個人情報の保管について",
      text: "弊社はお客様の個人情報を安全に保管することに尽力しており、不正アクセスから保護するための強力な暗号化措置を講じています。"
    },
    {
      title: "5. データ保護の権利について",
      items: [
        { label: "アクセス権", text: "お客様は、自己の個人情報にアクセスし、弊社が保有する情報のコピーを受け取ることができます。" },
        { label: "訂正権", text: "不正確または不完全な個人情報の訂正を要求することができます。" },
        { label: "削除権", text: "特定の状況下で、個人情報の削除を要求することができます。" },
        { label: "処理の制限", text: "特定の状況下で、個人情報の処理を制限することを要求することができます。" },
        { label: "データ移植性", text: "特定の状況下で、構造化された一般的な使用可能な形式での個人情報の提供を要求することができます。" },
        { label: "同意の撤回", text: "個人情報の処理に同意を与えている場合、いつでもその同意を撤回することができます。" },
      ]
    },
    {
      title: "6. クッキーとトラッキング技術について",
      text: "弊社のウェブサイトではクッキーや類似の技術を利用して、ウェブサイトの使用状況を分析し、パーソナライズされた体験を提供します。ブラウザの設定からクッキーの設定を管理できます。"
    },
    {
      title: "7. セキュリティとデータ保持について",
      items: [
        { label: "安全な保管", text: "お客様の個人情報は、不正アクセス、開示、変更、破壊から保護するための厳格な措置を講じて安全に保管されています。" },
        { label: "暗号化", text: "伝送および保存中のお客様のデータを保護するために、先進的な暗号化技術を使用しています。" },
        { label: "データ保持", text: "弊社は、このプライバシーポリシーに記載されている目的のため、または法律で要求される限り、お客様の個人情報を保持します。必要性がなくなった場合、迅速にお客様の個人情報を消去または匿名化します。" },
      ]
    },
    {
      title: "8. データ侵害通知について",
      items: [
        { label: "迅速な対応", text: "データ侵害が発生した場合、速やかに状況を評価し、対応します。" },
        { label: "タイムリーな通知", text: "侵害があったことを認識してから72時間以内に、関連当局にその事実を報告します。" },
        { label: "直接的なコミュニケーション", text: "侵害がお客様の権利と自由に高いリスクをもたらすと判断される場合、遅滞なくお客様に通知します。" },
      ]
    },
    {
      title: "9. 本ポリシーの更新について",
      text: "弊社は、プライバシーポリシーを定期的に見直し、必要に応じて更新します。変更がある場合は、Eメールでお客様に通知します。"
    },
    {
      title: "10. お問い合わせについて",
      text: "このプライバシーポリシーまたは弊社のデータ慣行に関する質問や懸念がある場合は、以下の連絡先までご連絡ください。\nお問い合わせ先: info@patlush.com"
    },
  ];

  const sectionsEn = [
    {
      title: "1. Types of Personal Data We Collect",
      items: [
        { label: "Personal Data", text: "This includes your name, email address, telephone number, company name, and other information provided when using our services." },
        { label: "Log Data", text: "Automatically collected information when you use our website or services, such as your IP address, browser type, operating system, and visited pages." },
        { label: "Cookies and Similar Technologies", text: 'We use cookies and similar technologies to collect data about your interactions with our website. Please refer to the "Cookies and Tracking Technologies" section below for more details.' },
      ]
    },
    {
      title: "2. Purposes for Using Your Personal Data",
      items: [
        { label: "Providing and Improving Services", text: "We use your information to deliver and enhance our services and website." },
        { label: "Communication", text: "We may use your contact information to respond to your inquiries, provide customer support, send requested materials, and provide updates about our services." },
        { label: "Marketing", text: "With your consent, we may send marketing communications about our products and services. You can opt out of these communications at any time." },
        { label: "Legal Compliance", text: "We use your information to comply with legal obligations and respond to lawful government requests." },
      ]
    },
    {
      title: "3. Sharing and Disclosure of Personal Data",
      items: [
        { label: "Service Providers", text: "We may provide your information to third-party service providers as necessary for service provision. These providers are obligated to protect your information." },
        { label: "Legal Requirements", text: "We may disclose your information to comply with legal obligations, protect our rights, privacy, safety, or property, or respond to lawful government requests." },
        { label: "With Your Consent", text: "We may share your information with third parties when you explicitly consent to such sharing." },
      ]
    },
    {
      title: "4. Storage of Personal Data",
      text: "We are committed to securely storing your personal data, employing robust encryption measures to protect it from unauthorized access."
    },
    {
      title: "5. Your Data Protection Rights",
      items: [
        { label: "Access Right", text: "You have the right to access your personal data, allowing you to receive a copy of the personal data we hold about you and verify its lawfulness." },
        { label: "Correction Right", text: "You can request the correction of inaccurate or incomplete personal data we hold about you." },
        { label: "Erasure Right", text: "You have the right to request the deletion of your personal data under certain circumstances." },
        { label: "Restriction of Processing", text: "You can request the restriction of the processing of your personal data in specific situations." },
        { label: "Data Portability", text: "In certain cases, you have the right to receive your personal data in a structured, commonly used, and machine-readable format." },
        { label: "Objection to Processing", text: "You can object to the processing of your personal data, especially for direct marketing purposes." },
        { label: "Withdrawal of Consent", text: "Where we rely on your consent to process your personal data, you have the right to withdraw that consent at any time." },
      ]
    },
    {
      title: "6. Cookies and Tracking Technologies",
      text: "We use cookies and similar technologies to analyze how visitors use our website, provide a personalized experience, and display targeted advertising. You can manage your cookie preferences through your browser settings."
    },
    {
      title: "7. Security and Data Retention",
      items: [
        { label: "Secure Storage", text: "Your personal data is securely stored with stringent measures to prevent unauthorized access, disclosure, alteration, or destruction." },
        { label: "Encryption", text: "We use advanced encryption techniques to protect your data during both transmission and storage." },
        { label: "Data Retention", text: "We retain your personal data as long as necessary for the purposes outlined in this Privacy Policy, or as required by law." },
      ]
    },
    {
      title: "8. Data Breach Notification",
      items: [
        { label: "Immediate Action", text: "In the event of a data breach, we will promptly assess and address the situation. We will notify the Office of the Personal Data Protection Committee without delay if the breach could potentially endanger your rights and freedoms." },
        { label: "Timely Notification", text: "Within 72 hours of becoming aware of the breach, we will inform the relevant authorities about the incident." },
        { label: "Direct Communication", text: "If the data breach is likely to result in a high risk to your rights and freedoms, we will notify you without delay, including details about the nature of the breach and the remedial measures we are taking." },
      ]
    },
    {
      title: "9. Updates to This Policy",
      text: "We periodically review and update this Privacy Policy as necessary. We will notify you of any changes via email."
    },
    {
      title: "10. Contact Us",
      text: "If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at: info@patlush.com"
    },
  ];

  const sectionsTh = [
    {
      title: "1. ประเภทของข้อมูลส่วนบุคคลที่เรารวบรวม",
      items: [
        { label: "ข้อมูลส่วนบุคคล", text: "รวมถึงชื่อ ที่อยู่อีเมล หมายเลขโทรศัพท์ และข้อมูลอื่น ๆ ของคุณที่ให้ไว้เมื่อใช้บริการของเรา" },
        { label: "ข้อมูลบันทึก", text: "ข้อมูลที่รวบรวมโดยอัตโนมัติเมื่อคุณใช้เว็บไซต์หรือบริการของเรา เช่น ที่อยู่ IP ประเภทเบราว์เซอร์ ระบบปฏิบัติการ และหน้าที่เยี่ยมชม" },
        { label: "คุกกี้และเทคโนโลยีที่คล้ายกัน", text: "เราใช้คุกกี้และเทคโนโลยีที่คล้ายกันเพื่อรวบรวมข้อมูลเกี่ยวกับการโต้ตอบของคุณกับเว็บไซต์ของเรา โปรดดูส่วน \"คุกกี้และเทคโนโลยีการติดตาม\" ด้านล่างสำหรับรายละเอียดเพิ่มเติม" },
      ]
    },
    {
      title: "2. วัตถุประสงค์ในการใช้ข้อมูลส่วนบุคคลของคุณ",
      items: [
        { label: "การให้บริการและปรับปรุงบริการ", text: "เราใช้ข้อมูลของคุณเพื่อส่งมอบและปรับปรุงบริการและเว็บไซต์ของเรา" },
        { label: "การสื่อสาร", text: "เราอาจใช้ข้อมูลติดต่อของคุณเพื่อตอบคำถามของคุณ ให้การสนับสนุนลูกค้า และส่งการอัปเดตเกี่ยวกับบริการของเรา" },
        { label: "การตลาด", text: "โดยการยินยอมของคุณ เราอาจส่งการสื่อสารทางการตลาดเกี่ยวกับผลิตภัณฑ์และบริการของเรา คุณสามารถเลือกไม่รับข่าวสารเหล่านี้ได้ตลอดเวลา" },
        { label: "การปฏิบัติตามกฎหมาย", text: "เราใช้ข้อมูลของคุณเพื่อปฏิบัติตามภาระผูกพันทางกฎหมายและตอบสนองต่อคำขอที่ถูกต้องตามกฎหมายของรัฐบาล" },
      ]
    },
    {
      title: "3. การแบ่งปันและการเปิดเผยข้อมูลส่วนบุคคล",
      items: [
        { label: "ผู้ให้บริการ", text: "เราอาจให้ข้อมูลของคุณแก่ผู้ให้บริการบุคคลที่สามตามความจำเป็นสำหรับการให้บริการ ผู้ให้บริการเหล่านี้มีหน้าที่ปกป้องข้อมูลของคุณ" },
        { label: "ข้อกำหนดทางกฎหมาย", text: "เราอาจเปิดเผยข้อมูลของคุณเพื่อให้สอดคล้องกับภาระผูกพันทางกฎหมาย ปกป้องสิทธิ์ ความเป็นส่วนตัว ความปลอดภัย หรือทรัพย์สินของเรา หรือตอบสนองต่อคำขอของรัฐบาลที่ชอบด้วยกฎหมาย" },
        { label: "โดยความยินยอมของคุณ", text: "เราอาจแบ่งปันข้อมูลของคุณกับบุคคลที่สามเมื่อคุณยินยอมอย่างชัดแจ้งต่อการแบ่งปันดังกล่าว" },
      ]
    },
    {
      title: "4. การจัดเก็บข้อมูลส่วนบุคคล",
      text: "เเรามุ่งมั่นที่จะจัดเก็บข้อมูลส่วนบุคคลของคุณอย่างปลอดภัย โดยใช้มาตรการการเข้ารหัสขั้นสูงเพื่อปกป้องข้อมูลจากการเข้าถึงโดยไม่ได้รับอนุญาต"
    },
    {
      title: "5. สิทธิ์ในการปกป้องข้อมูลของคุณ",
      items: [
        { label: "สิทธิ์การเข้าถึง", text: "คุณมีสิทธิ์ในการเข้าถึงข้อมูลส่วนบุคคลของคุณ โดยอนุญาตให้คุณรับสำเนาข้อมูลส่วนบุคคลเกี่ยวกับคุณที่เราเก็บไว้ และตรวจสอบความถูกต้องตามกฎหมาย" },
        { label: "สิทธิ์ในการแก้ไข", text: "คุณสามารถขอแก้ไขข้อมูลส่วนบุคคลที่ไม่ถูกต้องหรือไม่สมบูรณ์ที่เราเก็บไว้เกี่ยวกับคุณได้" },
        { label: "สิทธิ์ในการลบข้อมูล", text: "ในบางกรณี คุณมีสิทธิ์ขอให้ลบข้อมูลส่วนบุคคลของคุณ" },
        { label: "การจำกัดการประมวลผล", text: "คุณสามารถขอจำกัดการประมวลผลข้อมูลส่วนบุคคลของคุณในสถานการณ์เฉพาะได้" },
        { label: "ความสามารถในการพกพาข้อมูล", text: "ในบางกรณีคุณอาจร้องขอให้เราให้ข้อมูลส่วนบุคคลของคุณในรูปแบบที่มีโครงสร้างและใช้งานได้ทั่วไป" },
        { label: "การคัดค้านการประมวลผล", text: "คุณสามารถคัดค้านการประมวลผลข้อมูลส่วนบุคคลของคุณได้ โดยเฉพาะเพื่อวัตถุประสงค์โดยตรงทางการตลาด" },
        { label: "การเพิกถอนความยินยอม", text: "ในกรณีที่เราอาศัยความยินยอมของคุณในการประมวลผลข้อมูลส่วนบุคคลของคุณ คุณมีสิทธิ์ที่จะเพิกถอนความยินยอมนั้นได้ตลอดเวลา" },
      ]
    },
    {
      title: "6. คุกกี้และเทคโนโลยีการติดตาม",
      text: "เราใช้คุกกี้และเทคโนโลยีที่คล้ายกันเพื่อวิเคราะห์ว่าผู้เยี่ยมชมใช้เว็บไซต์ของเราอย่างไร มอบประสบการณ์ที่เป็นส่วนตัว และแสดงโฆษณาที่ตรงเป้าหมาย คุณสามารถจัดการการตั้งค่าคุกกี้ของคุณผ่านการตั้งค่าเบราว์เซอร์ของคุณ"
    },
    {
      title: "7. ความปลอดภัยและการเก็บรักษาข้อมูล",
      items: [
        { label: "พื้นที่จัดเก็บข้อมูลที่ปลอดภัย", text: "ข้อมูลส่วนบุคคลของคุณจะถูกเก็บไว้อย่างปลอดภัยด้วยมาตรการที่เข้มงวดเพื่อป้องกันการเข้าถึง การเปิดเผย การเปลี่ยนแปลง หรือการทำลายโดยไม่ได้รับอนุญาต" },
        { label: "การเข้ารหัส", text: "เราใช้เทคนิคการเข้ารหัสขั้นสูงเพื่อปกป้องข้อมูลของคุณระหว่างการส่งและการจัดเก็บ" },
        { label: "การเก็บรักษาข้อมูล", text: "เราเก็บรักษาข้อมูลส่วนบุคคลของคุณนานเท่าที่จำเป็นเพื่อวัตถุประสงค์ที่ระบุไว้ในนโยบายความเป็นส่วนตัวนี้ หรือตามที่กฎหมายกำหนด" },
      ]
    },
    {
      title: "8. การแจ้งเตือนการละเมิดข้อมูล",
      items: [
        { label: "การดำเนินการทันที", text: "ในกรณีที่มีการละเมิดข้อมูล เราจะประเมินและแก้ไขสถานการณ์ทันที เราจะแจ้งให้สำนักงานคณะกรรมการข้อมูลส่วนบุคคลทราบโดยไม่ชักช้าหากการละเมิดอาจเป็นอันตรายต่อสิทธิและเสรีภาพของคุณ" },
        { label: "การแจ้งเตือนทันเวลา", text: "ภายใน 72 ชั่วโมงหลังจากทราบถึงการละเมิด เราจะแจ้งให้หน่วยงานที่เกี่ยวข้องทราบเกี่ยวกับเหตุการณ์ดังกล่าว" },
        { label: "การสื่อสารโดยตรง", text: "หากการละเมิดข้อมูลมีแนวโน้มที่จะส่งผลให้เกิดความเสี่ยงสูงต่อสิทธิและเสรีภาพของคุณ เราจะแจ้งให้คุณทราบโดยไม่ชักช้า รวมถึงรายละเอียดเกี่ยวกับลักษณะของการละเมิดและมาตรการแก้ไขที่เรากำลังดำเนินการ" },
      ]
    },
    {
      title: "9. การปรับปรุงนโยบายนี้",
      text: "เราตรวจสอบและอัปเดตนโยบายความเป็นส่วนตัวนี้เป็นระยะตามความจำเป็น เราจะแจ้งให้คุณทราบถึงการเปลี่ยนแปลงใด ๆ ทางอีเมล"
    },
    {
      title: "10. ติดต่อเรา",
      text: "หากคุณมีคำถามหรือข้อกังวลเกี่ยวกับนโยบายความเป็นส่วนตัวนี้หรือหลักปฏิบัติด้านข้อมูลของเรา โปรดติดต่อเราได้ที่: info@patlush.com"
    },
  ];

  const sections = isEn ? sectionsEn : isTh ? sectionsTh : sectionsJa;

  return (
    <div className="privacy-page" style={{
      fontFamily: "'Noto Sans JP', sans-serif",
      color: "#001A3D",
      background: "#fff",
      minHeight: "100vh",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .privacy-page {
          background:
            radial-gradient(circle at 80% 8%, rgba(116, 105, 216, 0.1) 0, rgba(116, 105, 216, 0) 32%),
            linear-gradient(180deg, #fff 0%, #fbfaff 100%) !important;
        }
        .privacy-back-link {
          transition: color 0.18s ease;
        }
        .privacy-back-link:hover {
          color: #b88418 !important;
        }
        .privacy-lang-btn {
          font-size: 11px; font-weight: 600;
          color: #bbb; text-decoration: none; padding: 4px 4px;
          line-height: 1; transition: color 0.15s ease;
        }
        .privacy-lang-btn:hover { color: #061845; }
        .privacy-lang-btn.is-active {
          font-weight: 900; color: #061845;
          text-decoration: underline; text-underline-offset: 2px;
        }
        .privacy-content-card {
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid rgba(6, 24, 69, 0.1);
          border-radius: 8px;
          box-shadow: 0 14px 34px rgba(32, 28, 84, 0.05);
          padding: 40px;
        }
        .privacy-section {
          padding-top: 2px;
        }
        .privacy-section + .privacy-section {
          margin-top: 38px;
        }
        .privacy-section-title {
          font-size: 17px !important;
          font-weight: 900;
          color: #061845;
          margin-bottom: 16px;
          padding-left: 13px;
          border-left: 3px solid #d8a132;
          line-height: 1.55;
        }
        .privacy-text {
          font-size: 13.5px;
          color: #4b5568;
          line-height: 2;
        }
        .privacy-list {
          list-style: none;
          padding: 0;
        }
        .privacy-list-item {
          margin-bottom: 12px;
          font-size: 13.5px;
          color: #4b5568;
          line-height: 2;
        }
        .privacy-list-label {
          font-weight: 700;
          color: #061845;
        }
        @media (max-width: 768px) {
          .privacy-header {
            padding: 12px 18px !important;
          }
          .privacy-logo {
            height: 66px !important;
          }
          .privacy-main {
            padding: 42px 18px 52px !important;
          }
          .privacy-content-card {
            padding: 24px 20px !important;
          }
          .privacy-title {
            font-size: 25px !important;
            margin-bottom: 28px !important;
          }
          .privacy-lead {
            margin-bottom: 34px !important;
          }
          .privacy-section {
            padding: 0 !important;
          }
          .privacy-lead,
          .privacy-text,
          .privacy-list-item {
            font-size: 13px !important;
            line-height: 1.82 !important;
          }
          .privacy-section + .privacy-section {
            margin-top: 34px !important;
          }
          .privacy-section-title {
            font-size: 16px !important;
            margin-bottom: 14px !important;
            line-height: 1.5 !important;
          }
          .privacy-text {
            margin: 0 !important;
          }
          .privacy-list-item {
            margin-bottom: 10px !important;
          }
          .privacy-list-item:last-child {
            margin-bottom: 0 !important;
          }
        }
      `}</style>

      {/* ヘッダー */}
      <header className="privacy-header" style={{
        background: "rgba(255, 255, 255, 0.95)",
        borderBottom: "1px solid rgba(16, 30, 75, 0.08)",
        padding: "16px 24px",
        display: "flex",
        alignItems: "center",
        boxShadow: "0 10px 28px rgba(33, 26, 82, 0.04)",
      }}>
        <a href={root} style={{ textDecoration: "none" }}>
          <img className="privacy-logo" src="/nuntlogo.png" alt="Nunt" style={{ height: 90, width: "auto" }} />
        </a>

        {/* 言語切替 */}
        <div style={{ display: "flex", alignItems: "center", gap: 3, marginLeft: "auto", marginRight: 14 }}>
          {[['th','TH'],['en','EN'],['jp','JP']].map(([l, label], i) => (
            <span key={l} style={{ display: "flex", alignItems: "center" }}>
              {i > 0 && <span style={{ color: "#d0d0d0", fontSize: 10, margin: "0 2px" }}>/</span>}
              <a
                className={`privacy-lang-btn${language === l ? " is-active" : ""}`}
                href={switchLangUrl(l)}
                onClick={() => saveLangPref(l)}
              >{label}</a>
            </span>
          ))}
        </div>

        <a className="privacy-back-link" href={root} style={{
          fontSize: 13,
          color: "#061845",
          textDecoration: "none",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}>
          {backLink}
        </a>
      </header>

      {/* コンテンツ */}
      <main className="privacy-main" style={{
        maxWidth: 880,
        margin: "0 auto",
        padding: "64px 24px 72px",
      }}>
        <div className="privacy-content-card">
          <h1 className="privacy-title" style={{
            fontSize: 30, fontWeight: 900,
            color: "#061845", marginBottom: 36,
            paddingBottom: 16,
            borderBottom: "2px solid rgba(216, 161, 50, 0.72)",
            lineHeight: 1.4,
          }}>
            {pageTitle}
          </h1>

          <p className="privacy-lead" style={{ fontSize: 14, color: "#4b5568", lineHeight: 2, marginBottom: 42 }}>
            {leadText}
          </p>

          {sections.map((section, i) => (
            <section className="privacy-section" key={i}>
              <h2 className="privacy-section-title">
                {section.title}
              </h2>
              {section.text && (
                <p className="privacy-text" style={{ whiteSpace: "pre-line" }}>
                  {section.text}
                </p>
              )}
              {section.items && (
                <ul className="privacy-list">
                  {section.items.map((item, j) => (
                    <li className="privacy-list-item" key={j}>
                      <span className="privacy-list-label">{item.label}{sep}</span>
                      {item.text}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </main>

      {/* フッター */}
      <footer style={{
        background: "#F8F9FA",
        borderTop: "1px solid #e8e8e8",
        padding: "24px",
        textAlign: "center",
      }}>
        <p style={{ fontSize: 12, color: "#888" }}>© 2026 Patlush All Rights Reserved.</p>
      </footer>
    </div>
  );
}
